import React, { useEffect, useMemo, useState } from 'react';
import { Check, Clock, Loader2 } from 'lucide-react';

const KEYFRAMES_ID = 'clairads-loading-experience-keyframes';

function injectKeyframes() {
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes clairads-progress-sheen {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(220%); }
    }
    @keyframes clairads-row-shimmer {
      0% { background-position: 220% 0; }
      100% { background-position: -220% 0; }
    }
    @keyframes clairads-loader-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes clairads-loader-pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.08); opacity: 0.86; }
    }
    @keyframes clairads-loader-float {
      0% { opacity: 0; transform: translateY(14px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes clairads-loader-orbit {
      0% { transform: rotate(0deg) translateX(54px) rotate(0deg); }
      100% { transform: rotate(360deg) translateX(54px) rotate(-360deg); }
    }
  `;
  document.head.appendChild(style);
}

export function useElapsedSeconds(active, startTime) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active || !startTime) return undefined;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [active, startTime]);

  if (!startTime) return 0;
  return Math.max(0, Math.floor((now - startTime) / 1000));
}

export function formatElapsed(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (!mins) return `${secs}s`;
  return `${mins}m ${String(secs).padStart(2, '0')}s`;
}

function defaultStyles(accent) {
  return {
    panel: {
      background: '#FFFFFF',
      border: '1px solid #E5E7EB',
      borderRadius: 18,
      boxShadow: '0 18px 45px rgba(15, 22, 41, 0.08)',
    },
    accent,
    text: '#111827',
    textSoft: '#6B7280',
    border: '#E5E7EB',
    bg: '#F8F9FC',
    success: '#10B981',
  };
}

export function ProgressStagePanel({
  title,
  subtitle,
  stages,
  activeStageKey,
  progress,
  indeterminate = false,
  elapsedSeconds = 0,
  accent = '#1A56FF',
  contextItems = [],
  icon: CenterIcon,
  compact = false,
}) {
  useEffect(() => {
    injectKeyframes();
  }, []);

  const styles = defaultStyles(accent);
  const clampedProgress = Math.max(0, Math.min(100, Math.round(progress || 0)));
  const activeIndex = Math.max(0, stages.findIndex((stage) => stage.key === activeStageKey));
  const activeStage = stages[activeIndex] || stages[0] || {};
  const ActiveIcon = CenterIcon || activeStage.icon || Loader2;

  return (
    <div role="status" aria-live="polite" aria-busy="true" style={{ width: '100%', maxWidth: compact ? 620 : 680, margin: '0 auto', animation: 'clairads-loader-float 0.35s ease' }}>
      <div style={{ width: compact ? 92 : 116, height: compact ? 92 : 116, margin: compact ? '0 auto 22px' : '0 auto 30px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {[0, 1, 2].map((item) => (
          <div
            key={item}
            style={{
              position: 'absolute',
              width: item === 0 ? 8 : 7,
              height: item === 0 ? 8 : 7,
              borderRadius: '50%',
              background: item === 0 ? accent : item === 1 ? '#8B5CF6' : '#EC4899',
              animation: `clairads-loader-orbit ${3.2 + item * 0.45}s linear infinite`,
              animationDelay: `${item * 0.6}s`,
              opacity: 0.85,
            }}
          />
        ))}
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `1px solid ${accent}30` }} />
        <div
          style={{
            width: compact ? 60 : 74,
            height: compact ? 60 : 74,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${activeStage.color || accent}, ${accent})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'clairads-loader-pulse 2s ease-in-out infinite',
            boxShadow: `0 16px 42px ${(activeStage.color || accent)}35`,
          }}
        >
          <ActiveIcon size={compact ? 26 : 32} color="#fff" />
        </div>
      </div>

      <div style={{ textAlign: 'center', marginBottom: compact ? 18 : 24 }}>
        <h2 style={{ fontSize: compact ? 20 : 24, lineHeight: 1.15, fontWeight: 800, color: styles.text, margin: '0 0 8px' }}>
          {title}
        </h2>
        <p style={{ fontSize: 14, color: activeStage.color || accent, fontWeight: 700, margin: 0 }}>
          {subtitle || activeStage.label}
        </p>
      </div>

      <div style={{ ...styles.panel, padding: compact ? '18px 20px' : '22px 26px', textAlign: 'left' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 14, alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: styles.text }}>{indeterminate ? 'En curso' : `${clampedProgress}%`}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: styles.textSoft }}>
            <Clock size={14} />
            {formatElapsed(elapsedSeconds)}
          </div>
          <div style={{ fontSize: 12, color: styles.textSoft, fontWeight: 600 }}>
            {activeIndex + 1}/{stages.length}
          </div>
        </div>

        <div role="progressbar" aria-label={subtitle || activeStage.label || title} aria-valuemin={indeterminate ? undefined : 0} aria-valuemax={indeterminate ? undefined : 100} aria-valuenow={indeterminate ? undefined : clampedProgress} style={{ height: 10, borderRadius: 999, background: styles.bg, overflow: 'hidden', position: 'relative' }}>
          <div
            style={{
              width: indeterminate ? '100%' : `${clampedProgress}%`,
              height: '100%',
              borderRadius: 999,
              background: `linear-gradient(90deg, ${accent}, color-mix(in srgb, ${accent} 68%, white))`,
              transition: 'width 0.55s ease',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {clampedProgress < 100 && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: '36%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.36), transparent)',
                  animation: 'clairads-progress-sheen 1.6s ease-in-out infinite',
                }}
              />
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
          {stages.map((stage, index) => {
            const isDone = index < activeIndex || activeStageKey === 'complete';
            const isCurrent = index === activeIndex && activeStageKey !== 'complete';
            const StageIcon = stage.icon || Loader2;
            return (
              <div
                key={stage.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '28px 1fr auto',
                  alignItems: 'center',
                  gap: 10,
                  opacity: isDone || isCurrent ? 1 : 0.42,
                  transition: 'opacity 0.25s ease',
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: isDone ? styles.success : isCurrent ? (stage.color || accent) : styles.border,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                  }}
                >
                  {isDone ? <Check size={14} strokeWidth={3} /> : <StageIcon size={14} color={isCurrent ? '#fff' : styles.textSoft} />}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: isCurrent ? 800 : 650, color: isCurrent ? styles.text : styles.textSoft }}>
                    {stage.label}
                  </div>
                  {stage.detail && (
                    <div style={{ fontSize: 11, color: styles.textSoft, marginTop: 2 }}>{stage.detail}</div>
                  )}
                </div>
                {isCurrent && <Loader2 size={15} color={stage.color || accent} style={{ animation: 'clairads-loader-spin 0.8s linear infinite' }} />}
              </div>
            );
          })}
        </div>
      </div>

      {contextItems.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, maxWidth: 620 }}>
            {contextItems.filter(Boolean).map((item) => (
              <span
                key={item}
                style={{
                  fontSize: 12,
                  lineHeight: 1.3,
                  color: styles.textSoft,
                  background: '#FFFFFF',
                  border: `1px solid ${styles.border}`,
                  borderRadius: 999,
                  padding: '8px 12px',
                }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function LoadingRows({ rows = 4, accent = '#1A56FF', label = 'Cargando…' }) {
  useEffect(() => {
    injectKeyframes();
  }, []);

  const widths = useMemo(() => ['76%', '58%', '84%', '46%', '68%'], []);

  return (
    <div role="status" aria-live="polite" aria-busy="true" style={{ textAlign: 'center', padding: '42px 24px' }}>
      <Loader2 size={26} color={accent} style={{ animation: 'clairads-loader-spin 0.9s linear infinite', marginBottom: 12 }} />
      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 18 }}>{label}</div>
      <div style={{ maxWidth: 520, margin: '0 auto', display: 'grid', gap: 10 }}>
        {Array.from({ length: rows }).map((_, index) => (
          <div
            key={index}
            style={{
              height: 12,
              width: widths[index % widths.length],
              margin: '0 auto',
              borderRadius: 999,
              background: 'linear-gradient(90deg, #E5E7EB 25%, #F8F9FC 50%, #E5E7EB 75%)',
              backgroundSize: '220% 100%',
              animation: 'clairads-row-shimmer 1.3s ease-in-out infinite',
            }}
          />
        ))}
      </div>
    </div>
  );
}
